/**
 * Fusion du pré-remplissage (issu de l'audit) avec les réponses déjà saisies.
 *
 * Le pré-remplissage ne comble QUE les champs VIDES ou absents : une réponse
 * réelle (chaîne non vide, ou case/liste cochée) l'emporte toujours et n'est
 * jamais réécrite. Combler le vide — et pas seulement l'absence — est
 * nécessaire : le formulaire auto-enregistre tous ses champs, souvent à vide,
 * si bien qu'un simple `{...prefill, ...saved}` laisserait ces vides masquer
 * l'audit (c'est ce qui figeait le questionnaire à 0 %).
 */
function estVide(v: unknown): boolean {
  if (v == null) return true;
  if (typeof v === 'string') return v.trim() === '';
  if (Array.isArray(v)) return v.length === 0;
  return false;
}

/**
 * Sections répétables → champ-clé qui identifie une ligne. L'alignement par
 * INDEX est faux dès que les réponses sauvées portent des lignes dans un autre
 * ordre que le pré-remplissage recalculé (le volume de « E:\ » atterrissait
 * sur la ligne « C:\ ») : on aligne par IDENTITÉ de ligne, jamais par position.
 * Miroir exact de `align_repeatable_rows` (lib/questionnaire_prefill.py).
 */
const ROW_KEYFIELDS: Record<string, string> = {
  'data_sources': 'name', 'hosts': 'hostname', 'compute.gpu.details': 'modele',
  'rag_corpus': 'corpus', 'use_cases': 'name', 'models_prod': 'name',
  'compute.profils': 'profil',
};

export function alignRepeatableRows(
  saved: Record<string, unknown>,
  prefill: Record<string, string | string[]>,
): Record<string, string | string[]> {
  const out: Record<string, string | string[]> = { ...prefill };
  for (const [prefix, keyfield] of Object.entries(ROW_KEYFIELDS)) {
    const motif = new RegExp(
      `^${prefix.replace(/[.[\]]/g, (c) => '\\' + c)}\\[(\\d+)\\]\\.(.+)$`);
    const nouveaux = [...new Set(Object.keys(prefill)
      .map((k) => motif.exec(k)?.[1])
      .filter((i): i is string => i != null)
      .map(Number))].sort((a, b) => a - b);
    if (!nouveaux.length) continue;
    const anciens = new Map<number, string>();
    for (const [k, v] of Object.entries(saved)) {
      const m = motif.exec(k);
      if (m && m[2] === keyfield && !estVide(v)) anciens.set(Number(m[1]), String(v));
    }
    if (!anciens.size) continue;
    const cibles = new Map<number, number>();
    const pris = new Set<number>(anciens.keys());
    let libre = 0;
    for (const i of nouveaux) {
      const nom = prefill[`${prefix}[${i}].${keyfield}`];
      let cible = [...anciens.entries()].find(
        ([j, val]) => typeof nom === 'string' && val === nom
          && ![...cibles.values()].includes(j))?.[0];
      if (cible == null) {
        while (pris.has(libre) || [...cibles.values()].includes(libre)) libre += 1;
        cible = libre;
      }
      cibles.set(i, cible);
    }
    if (nouveaux.every((i) => cibles.get(i) === i)) continue;
    for (const k of Object.keys(out)) if (motif.test(k)) delete out[k];
    for (const [k, v] of Object.entries(prefill)) {
      const m = motif.exec(k);
      if (m) out[`${prefix}[${cibles.get(Number(m[1]))}].${m[2]}`] = v;
    }
  }
  return out;
}

export function mergePrefill(
  prefill: Record<string, string | string[]> | null | undefined,
  saved: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  const base = saved ?? {};
  const out: Record<string, unknown> = { ...base };
  for (const [k, v] of Object.entries(alignRepeatableRows(base, prefill ?? {}))) {
    if (estVide(out[k])) out[k] = v;
  }
  return out;
}
